// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { fetchEndUserTradeTracking } from '../../util/api';
import { useWalletSessionMaybe } from '../../context/WalletSessionContext';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './EndUserTradeTimeline.module.css';

const POLL_INTERVAL_MS = 5000;

const EndUserTradeTimeline = ({ className = null, isAuthenticated = false }) => {
  const walletSession = useWalletSessionMaybe();
  const canPoll =
    isAuthenticated &&
    (walletSession?.authMode === 'web3' || walletSession?.walletAddress);

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(Boolean(canPoll));
  const [error, setError] = useState(null);

  const loadTrades = useCallback(() => {
    if (!canPoll) {
      setTrades([]);
      setLoading(false);
      return Promise.resolve();
    }

    setError(null);
    return fetchEndUserTradeTracking()
      .then(payload => {
        setTrades(Array.isArray(payload?.trades) ? payload.trades : []);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load trade tracking');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [canPoll]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  useEffect(() => {
    if (!canPoll) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      loadTrades();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [canPoll, loadTrades]);

  if (!canPoll) {
    return null;
  }

  return (
    <section
      className={classNames(css.root, className)}
      data-testid="end-user-trade-timeline"
      aria-live="polite"
    >
      <header className={css.header}>
        <h2 className={css.title}>Your trade tracking</h2>
        <p className={css.subtitle}>Live status from the universal trade shadow ledger.</p>
      </header>

      {loading && trades.length === 0 ? (
        <div className={css.loading}>
          <IconSpinner />
          Loading trade status…
        </div>
      ) : null}

      {error ? <p className={css.error}>{error}</p> : null}

      {!loading && trades.length === 0 && !error ? (
        <p className={css.empty}>Fund a gig to see your delivery timeline here.</p>
      ) : null}

      <ul className={css.list}>
        {trades.map(trade => (
          <li key={trade.notice_id} className={css.card} data-testid={`trade-timeline-${trade.notice_id}`}>
            <div className={css.cardHeader}>
              <h3 className={css.cardTitle}>{trade.title}</h3>
              <span className={css.stateBadge}>{trade.trade_state.replace(/_/g, ' ')}</span>
            </div>

            <ol className={css.steps}>
              {(trade.steps || []).map(step => {
                const active = step.order <= (trade.current_step || 0);
                const current = step.state === trade.trade_state;
                return (
                  <li
                    key={step.state}
                    className={classNames(
                      css.step,
                      active ? css.stepActive : null,
                      current ? css.stepCurrent : null
                    )}
                  >
                    <span className={css.stepDot} aria-hidden />
                    <span className={css.stepLabel}>{step.label}</span>
                    {current && step.order < 4 ? (
                      <span className={css.stepArrow} aria-hidden>
                        →
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ol>

            {trade.handshake_pin ? (
              <div className={css.pinBlock} data-testid="handshake-pin-display">
                <span className={css.pinLabel}>Handoff PIN</span>
                <span className={css.pinValue}>{trade.handshake_pin}</span>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default EndUserTradeTimeline;
